/**
 * Product Gallery Navigation Sync
 * 
 * This component ensures proper synchronization between:
 * - Variant picker selection
 * - Product gallery navigation (thumbnails)
 * - Product gallery carousel (main images)
 * - Dynamic aria-label updates for accessibility
 * 
 * Features:
 * - Updates aria-labels dynamically to reflect current visible media count
 * - Syncs gallery navigation with variant picker
 * - Ensures the correct media is displayed based on selections
 * - Maintains accessibility with accurate "Item X of Y" labels
 */

class ProductGalleryNavigationSync {
  constructor() {
    this.initialized = false;
    this.galleryCleanupMap = new WeakMap(); // Track cleanup functions for each gallery
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all product galleries on the page
    const galleries = document.querySelectorAll('product-gallery');
    
    galleries.forEach(gallery => {
      this.setupGallerySync(gallery);
    });

    // Setup on section reload (Shopify theme editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        const galleries = document.querySelectorAll('product-gallery');
        galleries.forEach(gallery => {
          this.setupGallerySync(gallery);
        });
      });
    }

    this.initialized = true;
    console.log('ProductGalleryNavigationSync: Initialized');
  }

  setupGallerySync(gallery) {
    if (!gallery) return;

    // Clean up existing listeners if this gallery was already set up
    this.cleanupGallery(gallery);

    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;

    const formId = gallery.getAttribute('form');
    if (!formId) return;

    const form = document.forms[formId];
    if (!form) return;

    // Find variant-picker by form-id attribute (it's a sibling of the form, not a child)
    const variantPicker = document.querySelector(`variant-picker[form-id="${formId}"]`);
    const galleryNav = gallery.querySelector('product-gallery-navigation');

    // Store references for cleanup
    const abortController = new AbortController();
    const signal = abortController.signal;
    let currentCell = null; // Track the current cell from carousel:settle event

    // Update aria-labels on initial load
    this.updateAriaLabels(gallery);

    // Listen for variant changes and update aria-labels
    const variantChangeHandler = (event) => {
      setTimeout(() => {
        this.updateAriaLabels(gallery);
        this.syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell);
      }, 50);
    };
    form.addEventListener('variant:change', variantChangeHandler, { signal });

    // Listen for carousel changes and update aria-labels
    const carouselSettleHandler = (event) => {
      // Store the current cell from the event for accurate sync
      currentCell = event.detail?.cell || null;
      this.updateAriaLabels(gallery);
      this.syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell);
    };
    carousel.addEventListener('carousel:settle', carouselSettleHandler, { signal });

    // Listen for carousel filter events (when media visibility changes)
    const carouselFilterHandler = (event) => {
      setTimeout(() => {
        this.updateAriaLabels(gallery);
        this.syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell);
      }, 50);
    };
    carousel.addEventListener('carousel:filter', carouselFilterHandler, { signal });

    // Use MutationObserver to detect when media hidden attribute changes
    const observer = new MutationObserver((mutations) => {
      let hasVisibilityChange = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          hasVisibilityChange = true;
        }
      });
      
      if (hasVisibilityChange) {
        setTimeout(() => {
          this.updateAriaLabels(gallery);
          this.syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell);
        }, 50);
      }
    });

    // Observe all media items for visibility changes
    const allMedia = carousel.querySelectorAll('.product-gallery__media');
    allMedia.forEach(media => {
      observer.observe(media, { attributes: true, attributeFilter: ['hidden'] });
    });

    // Listen for gallery navigation thumbnail clicks
    const thumbnailClickHandlers = [];
    if (galleryNav) {
      const thumbnails = galleryNav.querySelectorAll('button[data-media-id]');
      thumbnails.forEach(thumbnail => {
        const clickHandler = () => {
          const mediaId = thumbnail.getAttribute('data-media-id');
          if (!mediaId) return;

          // Find the corresponding media in the carousel
          const targetMedia = carousel.querySelector(`[data-media-id="${mediaId}"]`);
          if (targetMedia && !targetMedia.hasAttribute('hidden')) {
            // Update aria-labels after navigation
            setTimeout(() => {
              this.updateAriaLabels(gallery);
            }, 100);
          }
        };
        thumbnail.addEventListener('click', clickHandler, { signal });
        thumbnailClickHandlers.push({ element: thumbnail, handler: clickHandler });
      });
    }

    // Store cleanup function
    this.galleryCleanupMap.set(gallery, {
      abortController,
      observer,
      currentCell: () => currentCell
    });

    // Initial sync
    this.syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell);

    console.log('ProductGalleryNavigationSync: Setup complete for gallery');
  }

  /**
   * Clean up event listeners and observers for a gallery
   */
  cleanupGallery(gallery) {
    if (!gallery) return;

    const cleanup = this.galleryCleanupMap.get(gallery);
    if (!cleanup) return;

    // Abort all event listeners
    if (cleanup.abortController) {
      cleanup.abortController.abort();
    }

    // Disconnect observer
    if (cleanup.observer) {
      cleanup.observer.disconnect();
    }

    this.galleryCleanupMap.delete(gallery);
    console.log('ProductGalleryNavigationSync: Cleaned up gallery');
  }

  /**
   * Update aria-labels for the currently selected media item only
   * This ensures the "Item X of Y" label is only on the active/selected media
   */
  updateAriaLabels(gallery) {
    if (!gallery) return;

    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;

    const allMedia = carousel.querySelectorAll('.product-gallery__media');
    const visibleMedia = Array.from(allMedia).filter(media => !media.hasAttribute('hidden'));
    const visibleCount = visibleMedia.length;

    // Get the currently displayed media
    const cleanup = this.galleryCleanupMap.get(gallery);
    const currentCell = cleanup && cleanup.currentCell ? cleanup.currentCell() : null;
    const selectedMedia = this.getCurrentlyDisplayedMedia(carousel, currentCell);

    // Clear aria-labels from all visible media items first
    visibleMedia.forEach((media) => {
      media.removeAttribute('aria-label');
    });

    // Only set aria-label on the currently selected media
    if (selectedMedia) {
      const selectedIndex = visibleMedia.indexOf(selectedMedia);
      if (selectedIndex !== -1) {
        const visibleIndex = selectedIndex + 1;
        const ariaLabel = this.getTranslatedAriaLabel(visibleIndex, visibleCount);
        selectedMedia.setAttribute('aria-label', ariaLabel);
        console.log(`ProductGalleryNavigationSync: Updated aria-label for selected media (${visibleIndex} of ${visibleCount})`);
      }
    }
  }

  /**
   * Get translated aria-label text
   * Tries to use theme translations, falls back to English
   */
  getTranslatedAriaLabel(index, count) {
    // Try to get translation from theme
    if (window.themeVariables && window.themeVariables.strings) {
      const template = window.themeVariables.strings.itemNthOfCount;
      if (template) {
        return template.replace('{{ index }}', index).replace('{{ count }}', count);
      }
    }

    // Fallback to English
    return `Item ${index} of ${count}`;
  }

  /**
   * Sync gallery navigation (thumbnails) with the current carousel state
   * Updates aria-current attributes to match the currently displayed media
   * @param {HTMLElement} gallery - The product gallery element
   * @param {HTMLElement} galleryNav - The gallery navigation element
   * @param {HTMLElement} currentCell - The current cell from carousel:settle event (optional)
   */
  syncGalleryNavigationWithCarousel(gallery, galleryNav, currentCell = null) {
    if (!gallery || !galleryNav) return;

    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;

    // Get the currently selected/visible media
    const selectedMedia = this.getCurrentlyDisplayedMedia(carousel, currentCell);
    if (!selectedMedia) return;

    const selectedMediaId = selectedMedia.getAttribute('data-media-id');
    if (!selectedMediaId) return;

    // Update thumbnail aria-current states
    const thumbnails = galleryNav.querySelectorAll('button[data-media-id]');
    thumbnails.forEach(thumbnail => {
      const thumbnailMediaId = thumbnail.getAttribute('data-media-id');
      const isSelected = thumbnailMediaId === selectedMediaId;
      thumbnail.setAttribute('aria-current', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Get the currently displayed media item in the carousel
   * This handles both scrollable carousels and grid layouts
   * @param {HTMLElement} carousel - The carousel element
   * @param {HTMLElement} currentCell - The current cell from carousel:settle event (optional)
   * @returns {HTMLElement|null} The currently displayed media element
   */
  getCurrentlyDisplayedMedia(carousel, currentCell = null) {
    if (!carousel) return null;

    // If we have a currentCell from the carousel:settle event, use it
    if (currentCell && !currentCell.hasAttribute('hidden')) {
      return currentCell;
    }

    // Check for media with is-selected class (updated by carousel on navigation)
    const selectedMedia = carousel.querySelector('.product-gallery__media.is-selected:not([hidden])');
    if (selectedMedia) return selectedMedia;

    // Check for media with is-initial class (default selected, used on initial load)
    const initialMedia = carousel.querySelector('.product-gallery__media.is-initial:not([hidden])');
    if (initialMedia) return initialMedia;

    // Find the first visible media item as last resort
    const visibleMedia = carousel.querySelectorAll('.product-gallery__media:not([hidden])');
    if (visibleMedia.length > 0) return visibleMedia[0];

    return null;
  }

  /**
   * Find variant by media ID in product data
   */
  findVariantByMediaId(product, mediaId) {
    if (!product || !product.variants || !mediaId) return null;

    const mediaIdNum = parseInt(mediaId);

    // Find variant with matching featured media
    for (const variant of product.variants) {
      if (variant.featured_media && variant.featured_media.id === mediaIdNum) {
        return variant;
      }
    }

    return null;
  }

  /**
   * Select a variant in the variant picker
   */
  selectVariant(variantPicker, variant, form) {
    if (!variantPicker || !variant || !form) return;

    // Find and update all option inputs to match the variant
    const optionFieldsets = variantPicker.querySelectorAll('.variant-picker__option');
    
    optionFieldsets.forEach((fieldset, optionIndex) => {
      const targetOptionValue = variant.options[optionIndex];
      if (!targetOptionValue) return;

      // Find the radio input that matches this option value
      const radioInputs = fieldset.querySelectorAll('input[type="radio"][data-option-position]');

      for (const input of radioInputs) {
        // Get the label associated with this input
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        
        if (!label) continue;

        // Extract text from label
        let labelText = '';
        
        const srOnly = label.querySelector('.sr-only');
        if (srOnly) {
          labelText = srOnly.textContent?.trim();
        } else {
          const labelClone = label.cloneNode(true);
          const nestedSpans = labelClone.querySelectorAll('span:not(.sr-only)');
          nestedSpans.forEach(span => {
            if (!span.classList.contains('block-swatch__color') && 
                !span.classList.contains('variant-carousel__placeholder-text')) {
              const textContent = span.textContent;
              span.replaceWith(document.createTextNode(textContent));
            } else {
              span.remove();
            }
          });
          labelText = labelClone.textContent?.trim();
        }
        
        if (!labelText) {
          labelText = label.getAttribute('title')?.trim();
        }

        // Compare option values (case-insensitive)
        if (labelText && 
            (labelText === targetOptionValue || 
             labelText.toLowerCase() === targetOptionValue.toLowerCase())) {
          
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    });
  }
}

// Initialize the sync on page load
if (!window.productGalleryNavigationSync) {
  window.productGalleryNavigationSync = new ProductGalleryNavigationSync();
}
